"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Image from "next/image";

/* ---------------- Dentist Images ---------------- */
const DENTISTS = [
  "/dentist/ashly.png",
  "/dentist/nitish.png",
  "/dentist/shifa.png",
  "/dentist/trishit1.png",
  "/dentist/swaroop.png",
  "/dentist/sanket.png",
  "/dentist/souvik.png",
  "/dentist/prerana.png",
  "/dentist/tejaswini.png",
];

export default function AssociatedDentists() {
  const [active, setActive] = useState(0);
  const total = DENTISTS.length;

  const [slideOffset, setSlideOffset] = useState(380); // desktop default

  const next = () => setActive((i) => (i + 1) % total);
  const prev = () => setActive((i) => (i - 1 + total) % total);

  /* --------- Responsive slide distance --------- */
  useEffect(() => {
    const updateOffset = () => {
      const width = window.innerWidth;

      if (width < 640) {
        setSlideOffset(240); // mobile
      } else if (width < 1024) {
        setSlideOffset(300); // tablet
      } else {
        setSlideOffset(380); // desktop
      }
    };

    updateOffset();
    window.addEventListener("resize", updateOffset);
    return () => window.removeEventListener("resize", updateOffset);
  }, []);

  /* --------- Preload Next Image --------- */
  useEffect(() => {
    const nextIndex = (active + 1) % total;
    const img = new window.Image();
    img.src = DENTISTS[nextIndex];
  }, [active, total]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-emerald-50/40 to-white py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">

        {/* ---------------- HEADER ---------------- */}
        <div className="mb-10 text-center">
          <span className="inline-block rounded-full border-2 border-emerald-300 bg-emerald-100 px-5 py-2 text-xs sm:text-sm font-bold text-emerald-700">
            🤝 Associated Dentists
          </span>

          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900">
            Specialists in the{" "}
            <span className="bg-gradient-to-r from-emerald-600 to-emerald-400 bg-clip-text text-transparent">
              spotlight
            </span>
          </h2>
        </div>

        {/* ---------------- SLIDER ---------------- */}
        <div className="relative flex h-[420px] sm:h-[480px] lg:h-[560px] items-center justify-center">
          {DENTISTS.map((img, i) => {
            let diff = i - active;
            if (diff > total / 2) diff -= total;
            if (diff < -total / 2) diff += total;

            if (Math.abs(diff) > 1) return null;

            return (
              <motion.div
                key={i}
                className="absolute transform-gpu will-change-transform"
                animate={{
                  x: diff * slideOffset,
                  scale: diff === 0 ? 1 : 0.92,
                  opacity: 1,
                  zIndex: diff === 0 ? 10 : 5,
                }}
                transition={{
                  duration: 0.55,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
<div className=" p-20 relative h-[360px] w-[380px] sm:h-[380px] sm:w-[300px] lg:h-[520px] lg:w-[420px]">
                  <Image
                    src={img}
                    alt="Associated Dentist"
                    fill
                    priority={diff === 0}
                    loading={diff === 0 ? "eager" : "lazy"}
                    sizes="(max-width: 640px) 90vw, (max-width: 1024px) 300px, 420px"
                    className="object-contain drop-shadow-2xl"
                  />
                </div>
              </motion.div>
            );
          })}

          {/* ---------------- ARROWS ---------------- */}
          <button
            onClick={prev}
            aria-label="Previous Dentist"
            className="absolute left-3 sm:left-6 top-1/2 z-20 -translate-y-1/2 grid size-10 sm:size-12 place-items-center rounded-full border-2 border-emerald-500 bg-white text-lg sm:text-xl text-emerald-600 shadow-lg transition hover:bg-emerald-600 hover:text-white"
          >
            ←
          </button>

          <button
            onClick={next}
            aria-label="Next Dentist"
            className="absolute right-3 sm:right-6 top-1/2 z-20 -translate-y-1/2 grid size-10 sm:size-12 place-items-center rounded-full border-2 border-emerald-500 bg-white text-lg sm:text-xl text-emerald-600 shadow-lg transition hover:bg-emerald-600 hover:text-white"
          >
            →
          </button>
        </div>

        {/* ---------------- DOTS ---------------- */}
        <div className="mt-6 flex justify-center gap-2">
          {DENTISTS.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="h-2 rounded-full transition-all"
              style={{
                width: i === active ? "28px" : "10px",
                backgroundColor: i === active ? "#229653" : "#d1d5db",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
